import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  History
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import {
  formatDateWithTimezone,
  formatDateTimeWithTimezone,
  utcToDisplayDateAndTime,
  displayDateTimeToUTC,
  getTimezoneLabel
} from '../../utils/timezoneUtils';

// Use same-origin API by default in browsers; allow override via REACT_APP_API_URL for local/dev tools
const API_URL = process.env.REACT_APP_API_URL || '/api';

const AvailabilityAndLeave = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [clientSettings, setClientSettings] = useState({ displayTimezone: 'Asia/Kolkata' });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data?.displayTimezone) setClientSettings((s) => ({ ...s, displayTimezone: res.data.displayTimezone }));
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';
  const tzLabel = getTimezoneLabel(displayTimezone);

  // Availability state
  const [availability, setAvailability] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [selectedAvailability, setSelectedAvailability] = useState(null);
  const [availabilityFormData, setAvailabilityFormData] = useState({
    startDate: null,
    endDate: null,
    availabilityStatus: 'AVAILABLE',
    reason: '',
    remarks: ''
  });

  // Combined list
  const [combinedLoading, setCombinedLoading] = useState(false);

  // Leave state
  const [requests, setRequests] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveFormData, setLeaveFormData] = useState({
    leaveStartDate: null,
    leaveEndDate: null,
    leaveType: 'ANNUAL',
    reason: '',
    remarks: ''
  });

  // History state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyTitle, setHistoryTitle] = useState('');

  useEffect(() => {
    // On mount, load both availability and leave so we can show a single combined list
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setCombinedLoading(true);
    await Promise.all([fetchAvailability(), fetchRequests(), fetchEntitlements()]);
    setCombinedLoading(false);
  };

  // ==================== Availability Functions ====================

  const fetchAvailability = async () => {
    setAvailabilityLoading(true);
    try {
      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/availability`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAvailability(response.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
      showToast('Failed to fetch availability entries', 'error');
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handleCreateAvailability = () => {
    setSelectedAvailability(null);
    setAvailabilityFormData({
      startDate: dayjs(),
      endDate: dayjs().add(1, 'day'),
      availabilityStatus: 'AVAILABLE',
      reason: '',
      remarks: ''
    });
    setAvailabilityDialogOpen(true);
  };

  const handleEditAvailability = (entry) => {
    setSelectedAvailability(entry);
    const startD = entry.startDate ? utcToDisplayDateAndTime(entry.startDate, displayTimezone).date : '';
    const endD = entry.endDate ? utcToDisplayDateAndTime(entry.endDate, displayTimezone).date : '';
    setAvailabilityFormData({
      startDate: startD ? dayjs(startD) : null,
      endDate: endD ? dayjs(endD) : null,
      availabilityStatus: entry.availabilityStatus,
      reason: entry.reason || '',
      remarks: entry.remarks || ''
    });
    setAvailabilityDialogOpen(true);
  };

  const handleSaveAvailability = async () => {
    try {
      if (!availabilityFormData.startDate || !availabilityFormData.endDate) {
        showToast('Please select start and end dates', 'error');
        return;
      }
      const reasonTrimmed = (availabilityFormData.reason || '').trim();
      if (availabilityFormData.availabilityStatus !== 'AVAILABLE' && !reasonTrimmed) {
        showToast('Reason is required when status is not Available', 'error');
        return;
      }

      const startStr = availabilityFormData.startDate?.format?.('YYYY-MM-DD') || availabilityFormData.startDate;
      const endStr = availabilityFormData.endDate?.format?.('YYYY-MM-DD') || availabilityFormData.endDate;
      const startDateISO = startStr ? (displayDateTimeToUTC(startStr, '00:00', displayTimezone) ?? undefined) : undefined;
      const endDateISO = endStr ? (displayDateTimeToUTC(endStr, '23:59', displayTimezone) ?? undefined) : undefined;
      const payload = {
        startDate: startDateISO,
        endDate: endDateISO,
        availabilityStatus: availabilityFormData.availabilityStatus,
        reason: availabilityFormData.availabilityStatus !== 'AVAILABLE' ? reasonTrimmed : undefined,
        remarks: availabilityFormData.remarks
      };

      if (selectedAvailability) {
        await axios.put(`${API_URL}/ops/crew-duty-roster/availability/${selectedAvailability._id}`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        showToast('Availability updated successfully', 'success');
      } else {
        await axios.post(`${API_URL}/ops/crew-duty-roster/availability`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        showToast('Availability request submitted for approval', 'success');
      }

      setAvailabilityDialogOpen(false);
      fetchAvailability();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to save availability', 'error');
    }
  };

  const handleDeleteAvailability = async (entry) => {
    if (!window.confirm('Are you sure you want to delete this availability entry?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/ops/crew-duty-roster/availability/${entry._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast('Availability entry deleted', 'success');
      fetchAll();
    } catch (error) {
      showToast('Failed to delete availability entry', 'error');
    }
  };

  const handleAcknowledgeAvailability = async (entry) => {
    try {
      await axios.put(`${API_URL}/ops/crew-duty-roster/availability/${entry._id}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast('Availability acknowledged', 'success');
      fetchAll();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to acknowledge availability', 'error');
    }
  };

  const getAvailabilityStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      case 'PENDING_APPROVAL': return 'warning';
      case 'ACKNOWLEDGED': return 'success';
      case 'PENDING_ACKNOWLEDGEMENT': return 'info';
      case 'AVAILABLE': return 'success';
      case 'UNAVAILABLE': return 'error';
      case 'ON_LEAVE': return 'info';
      case 'TRAINING': return 'warning';
      case 'MEDICAL': return 'error';
      default: return 'default';
    }
  };

  // ==================== Leave Functions ====================

  const fetchRequests = async () => {
    setLeaveLoading(true);
    try {
      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/leave-requests`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      showToast('Failed to fetch leave requests', 'error');
    } finally {
      setLeaveLoading(false);
    }
  };

  const fetchEntitlements = async () => {
    try {
      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/leave-entitlements/${user._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setEntitlements(response.data);
    } catch (error) {
      console.error('Error fetching leave entitlements:', error);
    }
  };

  const handleCreateLeave = () => {
    setLeaveFormData({
      leaveStartDate: dayjs().add(7, 'day'),
      leaveEndDate: dayjs().add(8, 'day'),
      leaveType: 'ANNUAL',
      reason: '',
      remarks: ''
    });
    setLeaveDialogOpen(true);
  };

  const handleSaveLeave = async () => {
    try {
      const startStr = leaveFormData.leaveStartDate?.format?.('YYYY-MM-DD') || leaveFormData.leaveStartDate;
      const endStr = leaveFormData.leaveEndDate?.format?.('YYYY-MM-DD') || leaveFormData.leaveEndDate;
      const leaveStartDateISO = startStr ? (displayDateTimeToUTC(startStr, '00:00', displayTimezone) ?? undefined) : undefined;
      const leaveEndDateISO = endStr ? (displayDateTimeToUTC(endStr, '23:59', displayTimezone) ?? undefined) : undefined;
      const payload = {
        leaveStartDate: leaveStartDateISO,
        leaveEndDate: leaveEndDateISO,
        leaveType: leaveFormData.leaveType,
        reason: leaveFormData.reason,
        remarks: leaveFormData.remarks
      };

      await axios.post(`${API_URL}/ops/crew-duty-roster/leave-request`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast('Leave request submitted successfully', 'success');
      setLeaveDialogOpen(false);
      fetchAll();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to submit leave request', 'error');
    }
  };

  const getLeaveStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      case 'PENDING': return 'warning';
      case 'CANCELLED': return 'default';
      default: return 'default';
    }
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    return dayjs(end).diff(dayjs(start), 'day') + 1;
  };

  // ==================== History Functions ====================

  const fetchAvailabilityHistory = async (entry) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const res = await axios.get(
        `${API_URL}/ops/crew-duty-roster/availability/${entry._id}/history`,
        { headers }
      );
      setHistoryTitle('Availability History');
      setHistoryItems(res.data || []);
      setHistoryDialogOpen(true);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to fetch availability history', 'error');
    }
  };

  const fetchLeaveHistory = async (request) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const res = await axios.get(
        `${API_URL}/ops/crew-duty-roster/leave-request/${request._id}/history`,
        { headers }
      );
      setHistoryTitle('Leave Request History');
      setHistoryItems(res.data || []);
      setHistoryDialogOpen(true);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to fetch leave request history', 'error');
    }
  };

  // Build unified list of availability + leave entries
  const unifiedItems = [
    ...(availability || []).map((entry) => ({
      _id: entry._id,
      type: 'AVAILABILITY',
      startDate: entry.startDate,
      endDate: entry.endDate,
      status: entry.status || entry.availabilityStatus,
      availabilityStatus: entry.availabilityStatus,
      reason: entry.reason,
      remarks: entry.remarks,
      rejectionReason: entry.rejectionReason
    })),
    ...(requests || []).map((request) => ({
      _id: request._id,
      type: 'LEAVE',
      startDate: request.leaveStartDate,
      endDate: request.leaveEndDate,
      status: request.status,
      leaveType: request.leaveType,
      reason: request.reason,
      remarks: request.remarks,
      rejectionReason: request.rejectionReason,
      approvedBy: request.approvedBy
    }))
  ].sort((a, b) => {
    // Sort by start date descending (most recent first)
    const aDate = a.startDate ? new Date(a.startDate).getTime() : 0;
    const bDate = b.startDate ? new Date(b.startDate).getTime() : 0;
    return bDate - aDate;
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Availability & Leave</Typography>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleCreateAvailability}
          >
            Add Availability
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateLeave}
          >
            Request Leave
          </Button>
        </Box>

        {/* Leave Entitlements (summary above table, if any) */}
        {entitlements.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {entitlements.map((entitlement) => (
              <Grid item xs={12} md={4} key={entitlement._id}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>Leave Entitlement</Typography>
                    <Typography variant="h6">{entitlement.leaveEntitlementDays} days</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {entitlement.leaveTakenDays} days taken, {entitlement.leaveRemainingDays} days remaining
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Rule: {entitlement.entitlementRule?.replace(/_/g, ' ')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Unified Availability + Leave List */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reason / Remarks</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {combinedLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Loading...</TableCell>
                </TableRow>
              ) : unifiedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">No availability or leave records found</TableCell>
                </TableRow>
              ) : (
                unifiedItems.map((item) => {
                  const isAvailability = item.type === 'AVAILABILITY';
                  return (
                    <TableRow key={`${item.type}-${item._id}`}>
                      <TableCell>
                        <Chip
                          label={isAvailability ? 'Availability' : 'Leave'}
                          color={isAvailability ? 'primary' : 'secondary'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{item.startDate ? (displayTimezone ? formatDateWithTimezone(item.startDate, displayTimezone) : dayjs(item.startDate).format('MMM DD, YYYY')) : '-'}</TableCell>
                      <TableCell>{item.endDate ? (displayTimezone ? formatDateWithTimezone(item.endDate, displayTimezone) : dayjs(item.endDate).format('MMM DD, YYYY')) : '-'}</TableCell>
                      <TableCell>
                        {isAvailability ? (
                          <Chip
                            label={(item.availabilityStatus || '').replace(/_/g, ' ')}
                            color={getAvailabilityStatusColor(item.availabilityStatus || 'AVAILABLE')}
                            size="small"
                          />
                        ) : (
                          <>
                            <Typography variant="body2">
                              {item.leaveType || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.startDate && item.endDate
                                ? `${calculateDays(item.startDate, item.endDate)} day(s)`
                                : ''}
                            </Typography>
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        {isAvailability ? (
                          item.status && (
                            <>
                              <Chip
                                label={item.status.replace(/_/g, ' ')}
                                color={getAvailabilityStatusColor(item.status)}
                                size="small"
                                sx={{ mb: item.status === 'REJECTED' && item.rejectionReason ? 1 : 0 }}
                              />
                              {item.status === 'REJECTED' && item.rejectionReason && (
                                <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                                  {item.rejectionReason}
                                </Alert>
                              )}
                            </>
                          )
                        ) : (
                          item.status && (
                            <Chip
                              icon={item.status === 'APPROVED' ? <CheckCircle /> : item.status === 'REJECTED' ? <Cancel /> : null}
                              label={item.status}
                              color={getLeaveStatusColor(item.status)}
                              size="small"
                            />
                          )
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.reason || '—'}
                        </Typography>
                        {item.remarks && (
                          <Typography variant="caption" color="text.secondary">
                            {item.remarks}
                          </Typography>
                        )}
                        {!isAvailability && item.status === 'APPROVED' && item.approvedBy && (
                          <Typography variant="caption" color="success.main" display="block">
                            Approved by {item.approvedBy.firstName} {item.approvedBy.lastName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {isAvailability ? (
                          <>
                            {item.status === 'PENDING_ACKNOWLEDGEMENT' && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => {
                                  const entry = availability.find(a => a._id === item._id);
                                  if (entry) handleAcknowledgeAvailability(entry);
                                }}
                                sx={{ mr: 1, mb: 0.5 }}
                              >
                                Acknowledge
                              </Button>
                            )}
                            {/* Allow editing/deleting only availability entries */}
                            <Tooltip title="Edit">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const entry = availability.find(a => a._id === item._id);
                                    if (entry) handleEditAvailability(entry);
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const entry = availability.find(a => a._id === item._id);
                                    if (entry) handleDeleteAvailability(entry);
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="View History">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const entry = availability.find(a => a._id === item._id);
                                    if (entry) fetchAvailabilityHistory(entry);
                                  }}
                                >
                                  <History fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        ) : (
                          // Leave rows: show history button
                          <Tooltip title="View History">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const request = requests.find(r => r._id === item._id);
                                if (request) fetchLeaveHistory(request);
                              }}
                            >
                              <History fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Availability Create/Edit Dialog */}
        <Dialog open={availabilityDialogOpen} onClose={() => setAvailabilityDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedAvailability ? 'Edit Availability' : 'Add Availability'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={tzLabel ? `Start Date (${tzLabel})` : 'Start Date'}
                  value={availabilityFormData.startDate}
                  onChange={(newValue) => setAvailabilityFormData({ ...availabilityFormData, startDate: newValue })}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={tzLabel ? `End Date (${tzLabel})` : 'End Date'}
                  value={availabilityFormData.endDate}
                  onChange={(newValue) => setAvailabilityFormData({ ...availabilityFormData, endDate: newValue })}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Availability Status</InputLabel>
                  <Select
                    value={availabilityFormData.availabilityStatus}
                    onChange={(e) => setAvailabilityFormData({ ...availabilityFormData, availabilityStatus: e.target.value })}
                    label="Availability Status"
                  >
                    <MenuItem value="AVAILABLE">Available</MenuItem>
                    <MenuItem value="UNAVAILABLE">Unavailable</MenuItem>
                    <MenuItem value="ON_LEAVE">On Leave</MenuItem>
                    <MenuItem value="TRAINING">Training</MenuItem>
                    <MenuItem value="MEDICAL">Medical</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {availabilityFormData.availabilityStatus !== 'AVAILABLE' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reason"
                    value={availabilityFormData.reason}
                    onChange={(e) => setAvailabilityFormData({ ...availabilityFormData, reason: e.target.value })}
                    required
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Remarks"
                  value={availabilityFormData.remarks}
                  onChange={(e) => setAvailabilityFormData({ ...availabilityFormData, remarks: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAvailabilityDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveAvailability} variant="contained">
              {selectedAvailability ? 'Update' : 'Submit'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Leave Request Dialog */}
        <Dialog open={leaveDialogOpen} onClose={() => setLeaveDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Request Leave</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={tzLabel ? `Leave Start Date (${tzLabel})` : 'Leave Start Date'}
                  value={leaveFormData.leaveStartDate}
                  onChange={(newValue) => setLeaveFormData({ ...leaveFormData, leaveStartDate: newValue })}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={tzLabel ? `Leave End Date (${tzLabel})` : 'Leave End Date'}
                  value={leaveFormData.leaveEndDate}
                  onChange={(newValue) => setLeaveFormData({ ...leaveFormData, leaveEndDate: newValue })}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Leave Type</InputLabel>
                  <Select
                    value={leaveFormData.leaveType}
                    onChange={(e) => setLeaveFormData({ ...leaveFormData, leaveType: e.target.value })}
                    label="Leave Type"
                  >
                    <MenuItem value="ANNUAL">Annual Leave</MenuItem>
                    <MenuItem value="SICK">Sick Leave</MenuItem>
                    <MenuItem value="PERSONAL">Personal Leave</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Reason"
                  value={leaveFormData.reason}
                  onChange={(e) => setLeaveFormData({ ...leaveFormData, reason: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Remarks (Optional)"
                  value={leaveFormData.remarks}
                  onChange={(e) => setLeaveFormData({ ...leaveFormData, remarks: e.target.value })}
                />
              </Grid>
              {leaveFormData.leaveStartDate && leaveFormData.leaveEndDate && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    Requesting {calculateDays(leaveFormData.leaveStartDate, leaveFormData.leaveEndDate)} days of leave
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLeaveDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveLeave}
              variant="contained"
              disabled={!leaveFormData.leaveStartDate || !leaveFormData.leaveEndDate || !leaveFormData.reason}
            >
              Submit Request
            </Button>
          </DialogActions>
        </Dialog>

        {/* Audit History Dialog */}
        <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{historyTitle || 'Change History'}</DialogTitle>
          <DialogContent dividers>
            {historyItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No history records found.
              </Typography>
            ) : (
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {historyItems.map((h) => (
                  <Box key={h.id} sx={{ mb: 2, p: 1.5, border: '1px solid #eee', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {h.action} by {h.actor?.name || 'System'} ({h.actor?.role || 'N/A'}) on{' '}
                      {displayTimezone ? formatDateTimeWithTimezone(h.createdAt, null, displayTimezone) : dayjs(h.createdAt).format('DD MMM YYYY HH:mm')}
                    </Typography>
                    {h.meta && h.meta.comment && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Comment: {h.meta.comment}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AvailabilityAndLeave;

